import { motion } from "framer-motion";
import {
  ArrowUpRight, ArrowDownLeft, ChevronRight, RefreshCw,
} from "lucide-react";
import { format, parseISO, subDays, isAfter } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

// FIN-1 — AtividadeRecenteCard
// Preview das últimas 5 transações + saldo do período + 2 mini-pills
// (entradas/saídas). Footer "Ver todas →" → Extrato (futuro FIN-2).

const fmtBRL = (v, sign = "") => {
  const abs = Math.abs(Number(v) || 0);
  const formatted = abs.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  return sign === "-" ? `−${formatted}` : sign === "+" ? `+${formatted}` : formatted;
};

function fmtDate(d) {
  if (!d) return "";
  try {
    const dt = d.includes("T") ? parseISO(d) : parseISO(`${d}T00:00:00`);
    return format(dt, "dd MMM", { locale: ptBR });
  } catch { return d; }
}

export default function AtividadeRecenteCard({ recent, loading, onSeeAll }) {
  const { user } = useAuth();
  const userId = user?.id;
  const all = Array.isArray(recent?.transactions) ? recent.transactions : [];
  // Filtro client-side por user (endpoint não filtra — limitação pré-existente)
  const mine = userId ? all.filter(t => !t.userId || t.userId === userId) : all;
  const items = mine.slice(0, 5);

  const sevenDaysAgo = subDays(new Date(), 7);
  const last7d = mine.filter(t => {
    if (!t.date) return false;
    try { return isAfter(parseISO(`${t.date}T00:00:00`), sevenDaysAgo); }
    catch { return false; }
  });
  const inc7d = last7d.filter(t => t.type === "income").reduce((s, t) => s + (t.amount || 0), 0);
  const exp7d = last7d.filter(t => t.type === "expense").reduce((s, t) => s + (t.amount || 0), 0);
  const periodBalance = inc7d - exp7d;

  return (
    <Card variant="elevated" className="glass-card glow-hover p-5 md:p-6 flex flex-col" data-testid="atividade-recente-card">
      <div className="mb-4">
        <span className="text-caption font-semibold uppercase text-brand-cyan/80" style={{ letterSpacing: "0.12em" }}>
          Atividade recente
        </span>
        <p className="text-micro text-foreground-subtle mt-0.5">
          {last7d.length} transaç{last7d.length === 1 ? "ão" : "ões"} · últimos 7 dias
        </p>
        <p className="text-caption text-foreground-muted mt-1.5 tabular-nums">
          Saldo do período: <span className={cn("font-semibold", periodBalance >= 0 ? "text-brand-green" : "text-status-error")}>
            {fmtBRL(periodBalance, periodBalance >= 0 ? "+" : "-")}
          </span>
        </p>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <Badge variant="outline" className="text-brand-green border-brand-green/30 bg-brand-green/5">
          <ArrowDownLeft className="w-3 h-3 mr-1" />
          {fmtBRL(inc7d, "+")}
        </Badge>
        <Badge variant="outline" className="text-status-error border-status-error/30 bg-status-error/5">
          <ArrowUpRight className="w-3 h-3 mr-1" />
          {fmtBRL(exp7d, "-")}
        </Badge>
      </div>

      {loading ? (
        <ul className="space-y-2">
          {[0, 1, 2, 3].map(i => (
            <li key={i} className="h-12 rounded-lg bg-surface-card-hover/40 animate-pulse" />
          ))}
        </ul>
      ) : items.length === 0 ? (
        <div className="flex-1 flex items-center justify-center py-6 text-center">
          <div>
            <p className="text-sm text-foreground-muted">Sem transações ainda</p>
            <p className="text-caption text-foreground-subtle">Registre a primeira em Extrato.</p>
          </div>
        </div>
      ) : (
        <ul className="space-y-1 flex-1" data-testid="atividade-list">
          {items.map((t, i) => {
            const isExpense = t.type === "expense";
            const Icon = isExpense ? ArrowUpRight : ArrowDownLeft;
            const iconBg = isExpense ? "bg-status-error/10 text-status-error" : "bg-brand-green/10 text-brand-green";
            const valueColor = isExpense ? "text-status-error" : "text-brand-green";
            return (
              <motion.li
                key={t.id || i}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04, duration: 0.2 }}
                className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-surface-card-hover transition-colors"
              >
                <div className={cn("w-8 h-8 rounded-md flex items-center justify-center shrink-0", iconBg)}>
                  <Icon className="w-3.5 h-3.5" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-foreground truncate uppercase" style={{ letterSpacing: "0.02em" }}>
                    {t.description || "(sem descrição)"}
                  </p>
                  <p className="text-micro text-foreground-subtle">
                    {fmtDate(t.date)} · {t.category || "outros"}
                  </p>
                </div>
                <div className={cn("font-mono text-xs font-bold tabular-nums shrink-0", valueColor)}>
                  {fmtBRL(t.amount, isExpense ? "-" : "+")}
                </div>
              </motion.li>
            );
          })}
        </ul>
      )}

      <div className="mt-4 pt-3 border-t border-subtle">
        <button
          type="button"
          onClick={onSeeAll}
          className="inline-flex items-center gap-1 text-caption text-brand-cyan hover:text-brand-cyan/80 transition-colors"
          data-testid="atividade-see-all"
        >
          Ver todas
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </Card>
  );
}
